import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { State, getDashboardReport, getDashboardOptions } from '../../reducers';
import { State as ReportState } from '../../reducers/dashboard/reducers-report';
import {
  filter,
  tap,
  switchMap,
  map,
  shareReplay,
  withLatestFrom,
  catchError,
  distinctUntilChanged
} from 'rxjs/operators';
import * as ReportActions from '../../actions/dashboard/actions-report';
import { Observable, combineLatest } from 'rxjs';
import { Report } from '../../models/dashboard/report';
import { BackendService } from './backend.service';

/**
 * Subscribe to the public report$ property wherever you need to retrieve a report
 * Dispatch Update action if you need to fetch new value
 */
@Injectable({
  providedIn: 'root'
})
export class ReportService {
  private reportState$: Observable<ReportState>;
  private fetchedReportState$: Observable<Report | any>;
  private report$: Observable<ReportState>;

  constructor(private readonly store: Store<State>, private readonly backedService: BackendService) {}

  public getReport(): Observable<ReportState> {
    if (!this.report$) {
      this.initObservables();
      this.report$ = combineLatest([this.fetchedReportState$, this.reportState$]).pipe(
        map(([_, reportState]) => reportState),
        distinctUntilChanged(),
        shareReplay(1)
      );
    }

    return this.report$;
  }

  private initObservables() {
    this.reportState$ = this.store.pipe(select(getDashboardReport));
    this.fetchedReportState$ = this.reportState$.pipe(
      filter(reportState => reportState.updateRequired),
      withLatestFrom(this.store.pipe(select(getDashboardOptions))),
      map(([_, optionsState]) => optionsState),
      filter(options => !!options.filters),
      switchMap(options => this.backedService.getDashboardData(options)),
      catchError(error => {
        this.store.dispatch(new ReportActions.Receive({ report: {} as Report, errorMessage: error }));
        return this.fetchedReportState$;
      }),
      tap((response: Report) => {
        this.store.dispatch(new ReportActions.Receive({ report: response, errorMessage: '' }));
      })
    );
  }
}
